import React, { useState } from 'react';
import { Upload, CheckCircle2, AlertCircle, Loader2, TrendingUp, Activity, RefreshCw, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function AdminUploadPage() {
  // Top Picks state
  const [topPicksData, setTopPicksData] = useState('');
  const [topPicksPreview, setTopPicksPreview] = useState([]);
  const [topPicksLoading, setTopPicksLoading] = useState(false);
  const [topPicksSuccess, setTopPicksSuccess] = useState(false);
  const [topPicksError, setTopPicksError] = useState('');

  // Active Stocks state
  const [activeData, setActiveData] = useState('');
  const [activePreview, setActivePreview] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeSuccess, setActiveSuccess] = useState(false);
  const [activeError, setActiveError] = useState('');

  // Admin action states
  const [priceRefreshing, setPriceRefreshing] = useState(false);
  const [priceRefreshMsg, setPriceRefreshMsg] = useState('');
  const [financialUpdating, setFinancialUpdating] = useState(false);
  const [financialUpdateMsg, setFinancialUpdateMsg] = useState('');

  const getAuthHeader = () => {
    const token = localStorage.getItem('tiwistock_access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const refreshPrices = async () => {
    setPriceRefreshing(true);
    setPriceRefreshMsg('');
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      });
      const data = await res.json();
      if (res.ok) {
        setPriceRefreshMsg('Price refresh started! Takes ~5 minutes in background.');
      } else {
        setPriceRefreshMsg(data.detail || 'Failed');
      }
    } catch {
      setPriceRefreshMsg('Network error');
    } finally {
      setPriceRefreshing(false);
    }
  };

  const updateFinancialStrength = async () => {
    setFinancialUpdating(true);
    setFinancialUpdateMsg('');
    try {
      const res = await fetch(`${API_URL}/api/financial-strength/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      });
      const data = await res.json();
      if (res.ok) {
        setFinancialUpdateMsg('Financial analysis started! Takes ~15 minutes in background.');
      } else {
        setFinancialUpdateMsg(data.detail || 'Failed');
      }
    } catch {
      setFinancialUpdateMsg('Network error');
    } finally {
      setFinancialUpdating(false);
    }
  };

  const parseTopPicks = () => {
    console.log('🔍 Parse button clicked!');
    console.log('📝 Data length:', topPicksData.length);
    console.log('📝 First 200 chars:', topPicksData.substring(0, 200));
    
    try {
      const lines = topPicksData.trim().split('\n');
      console.log('📊 Total lines:', lines.length);
      console.log('📊 First 10 lines:', lines.slice(0, 10));
      
      const parsed = [];
      
      // Parse MarketIndex format: 10 lines per stock
      for (let i = 0; i < lines.length; i += 10) {
        if (i + 9 >= lines.length) {
          console.log(`⚠️ Not enough lines at position ${i}`);
          break;
        }
        
        const symbol = lines[i].trim();
        const name = lines[i + 1].trim();
        const price = parseFloat(lines[i + 2].replace('$', '').trim());
        const consensus = lines[i + 3].trim();
        const buy = parseInt(lines[i + 4].trim());
        const hold = parseInt(lines[i + 5].trim());
        const sell = parseInt(lines[i + 6].trim());
        const target = parseFloat(lines[i + 7].replace('$', '').trim());
        const potential = parseFloat(lines[i + 8].replace('+', '').replace('%', '').trim());
        const lastUpdate = lines[i + 9].trim();
        
        console.log(`✓ Parsed stock: ${symbol} - ${name}`);
        
        // Keep every uploaded row regardless of consensus rating.
        // Score is buy_count × potential, so Hold/Sell/Strong Sell rows (with 0 buys)
        // naturally fall to the bottom but remain visible in Top Picks.
        const score = (buy || 0) * (potential || 0);
        parsed.push({
          symbol, name, price, consensus, buy, hold, sell, target, potential,
          score: Math.round(score * 100) / 100, lastUpdate
        });
      }
      
      console.log('✅ Successfully parsed', parsed.length, 'stocks');
      parsed.sort((a, b) => b.score - a.score);
      setTopPicksPreview(parsed);
      setTopPicksError('');
    } catch (err) {
      console.error('❌ Parse error:', err);
      setTopPicksError('Failed to parse data. Please check the format.');
      setTopPicksPreview([]);
    }
  };

  const parseActiveStocks = () => {
    try {
      const lines = activeData.trim().split('\n').filter(line => line.trim());
      const parsed = [];
      
      // Skip header line if present
      let startIdx = 0;
      if (lines[0] && (lines[0].toLowerCase().includes('code') || lines[0].toLowerCase().includes('company'))) {
        startIdx = 1;
      }
      
      // Parse 3-line format: ticker, company name, price data
      // OR single-line format (backward compatible)
      let i = startIdx;
      while (i < lines.length) {
        const line = lines[i].trim();
        if (!line) { i++; continue; }
        
        // Check if this line looks like a ticker code (1-4 chars, uppercase/digits)
        const tickerMatch = line.replace(/\t/g, '').trim();
        const isTickerLine = /^[A-Z0-9]{1,4}$/.test(tickerMatch);
        
        if (isTickerLine && i + 2 < lines.length) {
          // 3-line format: ticker, company name, data line
          const symbol = tickerMatch;
          const companyName = lines[i + 1].trim();
          let dataLine = lines[i + 2].trim();
          
          // Skip SUSPENDED or TRADING HALT stocks
          if (dataLine.startsWith('SUSPENDED') || dataLine.startsWith('TRADING HALT')) {
            i += 3;
            continue;
          }
          
          // Parse data line: $3.65  -0.35  -8.75%  $4.35  $3.62  11,117,001  $2.2B  +1,227.27%
          const parts = dataLine.split(/\t+/).map(p => p.trim()).filter(p => p);
          
          if (parts.length >= 6) {
            const price = parseFloat(parts[0].replace('$', '').replace(',', ''));
            const changePct = parseFloat(parts[2].replace('%', '').replace('+', ''));
            const volumeText = parts[5].replace(/,/g, '');
            const mktCapText = parts.length >= 7 ? parts[6] : 'N/A';
            
            let volume = 0;
            if (isNaN(parseFloat(volumeText))) {
              volume = 0;
            } else {
              volume = parseFloat(volumeText);
            }
            
            if (price > 0) {
              parsed.push({
                symbol,
                name: companyName,
                price,
                change_pct: isNaN(changePct) ? 0 : changePct,
                volume: Math.round(volume),
                value_traded: Math.round(price * volume),
                market_cap: mktCapText,
                sector: 'Various'
              });
            }
          }
          i += 3;
        } else {
          // Fallback: try single-line tab-separated format
          const parts = line.split(/\t+|\s{2,}/).map(p => p.trim());
          if (parts.length >= 6) {
            const name = parts[0];
            const price = parseFloat(parts[1]);
            const changePct = parseFloat(parts[4]?.replace('%', '') || '0');
            const volumeText = parts[5] || '0';
            
            let volume = 0;
            if (volumeText.includes('M')) {
              volume = parseFloat(volumeText.replace('M', '')) * 1_000_000;
            } else if (volumeText.includes('K')) {
              volume = parseFloat(volumeText.replace('K', '')) * 1_000;
            } else if (volumeText.includes('B')) {
              volume = parseFloat(volumeText.replace('B', '')) * 1_000_000_000;
            } else {
              volume = parseFloat(volumeText.replace(/,/g, ''));
            }
            
            const symbol = name.split(' ')[0].toUpperCase();
            if (price > 0) {
              parsed.push({
                symbol, name, price, change_pct: isNaN(changePct) ? 0 : changePct,
                volume: Math.round(volume),
                value_traded: Math.round(price * volume),
                market_cap: 'N/A', sector: 'Various'
              });
            }
          }
          i++;
        }
      }
      
      parsed.sort((a, b) => b.value_traded - a.value_traded);
      setActivePreview(parsed);
      setActiveError(parsed.length === 0 ? 'No stocks parsed. Check the data format.' : '');
    } catch (err) {
      setActiveError('Failed to parse data. Please check the format.');
      setActivePreview([]);
    }
  };

  const uploadTopPicks = async () => {
    setTopPicksLoading(true);
    setTopPicksError('');
    setTopPicksSuccess(false);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/upload-marketindex`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stocks: topPicksPreview })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTopPicksSuccess(true);
        setTimeout(() => {
          setTopPicksData('');
          setTopPicksPreview([]);
          setTopPicksSuccess(false);
        }, 3000);
      } else {
        setTopPicksError(data.message || 'Upload failed');
      }
    } catch (err) {
      setTopPicksError('Network error. Please try again.');
    } finally {
      setTopPicksLoading(false);
    }
  };

  const uploadActiveStocks = async () => {
    setActiveLoading(true);
    setActiveError('');
    setActiveSuccess(false);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/upload-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stocks: activePreview })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setActiveSuccess(true);
        setTimeout(() => {
          setActiveData('');
          setActivePreview([]);
          setActiveSuccess(false);
        }, 3000);
      } else {
        setActiveError(data.message || 'Upload failed');
      }
    } catch (err) {
      setActiveError('Network error. Please try again.');
    } finally {
      setActiveLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Upload data and manage background tasks
          </p>
        </div>

        {/* Admin Actions */}
        <div className="border-2 border-blue-500/20 rounded-lg p-6 bg-card/50">
          <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[250px]">
              <button
                onClick={refreshPrices}
                disabled={priceRefreshing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                data-testid="refresh-prices-btn"
              >
                <RefreshCw className={`w-4 h-4 ${priceRefreshing ? 'animate-spin' : ''}`} />
                {priceRefreshing ? 'Starting...' : 'Refresh All Prices'}
              </button>
              <p className="text-xs text-muted-foreground mt-1">Updates current prices from Yahoo Finance (~5 min)</p>
              {priceRefreshMsg && <p className="text-xs text-green-400 mt-1">{priceRefreshMsg}</p>}
            </div>
            <div className="flex-1 min-w-[250px]">
              <button
                onClick={updateFinancialStrength}
                disabled={financialUpdating}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                data-testid="update-financial-btn"
              >
                <BarChart3 className={`w-4 h-4 ${financialUpdating ? 'animate-pulse' : ''}`} />
                {financialUpdating ? 'Starting...' : 'Update Financial Strength'}
              </button>
              <p className="text-xs text-muted-foreground mt-1">Re-analyses 250+ stocks on 10 metrics (~15 min)</p>
              {financialUpdateMsg && <p className="text-xs text-green-400 mt-1">{financialUpdateMsg}</p>}
            </div>
          </div>
        </div>

        {/* TOP PICKS SECTION */}
        <div className="border-2 border-primary/20 rounded-lg p-6 bg-card/50">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Top Picks (MarketIndex)</h2>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 mb-4 text-sm">
            <p className="font-semibold mb-2">Format: 10 lines per stock</p>
            <code className="text-xs">Symbol → Name → Price → Consensus → Buy → Hold → Sell → Target → Potential% → Date</code>
          </div>

          <textarea
            value={topPicksData}
            onChange={(e) => setTopPicksData(e.target.value)}
            placeholder="360&#10;Life360 Inc&#10;$18.10&#10;Strong Buy&#10;8&#10;0&#10;0&#10;$36.80&#10;+103.31%&#10;16 Mar 2026"
            className="w-full h-48 bg-background border border-border rounded-lg p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary mb-4"
          />
          
          <div className="flex gap-3">
            <button onClick={parseTopPicks} disabled={!topPicksData.trim()}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50">
              Parse Data
            </button>
            
            {topPicksPreview.length > 0 && (
              <button onClick={uploadTopPicks} disabled={topPicksLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2">
                {topPicksLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : 
                  <><Upload className="w-4 h-4" /> Upload ({topPicksPreview.length})</>}
              </button>
            )}
          </div>

          {topPicksSuccess && (
            <div className="bg-green-600/10 border border-green-600/20 rounded-lg p-3 mt-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-green-600 font-medium text-sm">Top Picks updated!</span>
            </div>
          )}
          {topPicksError && (
            <div className="bg-red-600/10 border border-red-600/20 rounded-lg p-3 mt-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-600 text-sm">{topPicksError}</span>
            </div>
          )}
          {topPicksPreview.length > 0 && (
            <div className="mt-4 text-sm text-muted-foreground">
              Preview: {topPicksPreview.slice(0, 3).map(s => `${s.symbol} (${s.score})`).join(', ')}
            </div>
          )}
        </div>

        {/* ACTIVE STOCKS SECTION */}
        <div className="border-2 border-blue-500/20 rounded-lg p-6 bg-card/50">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-6 h-6 text-blue-500" />
            <h2 className="text-2xl font-bold">Active Stocks</h2>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 mb-4 text-sm">
            <p className="font-semibold mb-2">Format: Tab or space-separated</p>
            <code className="text-xs">Name | Last | High | Low | Chg% | Vol | Upside | Time</code>
          </div>

          <textarea
            value={activeData}
            onChange={(e) => setActiveData(e.target.value)}
            placeholder="BHP Group Ltd    47.47    47.77    46.59    -1.82%    7.76M    3.99    16:04:59"
            className="w-full h-48 bg-background border border-border rounded-lg p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          />
          
          <div className="flex gap-3">
            <button onClick={parseActiveStocks} disabled={!activeData.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              Parse Data
            </button>
            
            {activePreview.length > 0 && (
              <button onClick={uploadActiveStocks} disabled={activeLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2">
                {activeLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : 
                  <><Upload className="w-4 h-4" /> Upload ({activePreview.length})</>}
              </button>
            )}
          </div>

          {activeSuccess && (
            <div className="bg-green-600/10 border border-green-600/20 rounded-lg p-3 mt-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-green-600 font-medium text-sm">Active Stocks updated!</span>
            </div>
          )}
          {activeError && (
            <div className="bg-red-600/10 border border-red-600/20 rounded-lg p-3 mt-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-600 text-sm">{activeError}</span>
            </div>
          )}
          {activePreview.length > 0 && (
            <div className="mt-4 text-sm text-muted-foreground">
              Preview: {activePreview.slice(0, 3).map(s => `${s.symbol} ($${(s.value_traded / 1_000_000).toFixed(1)}M)`).join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
